import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Pencil, Ban, CheckCircle } from "lucide-react";
import { formatDate } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import EditFamilyDialog from './EditFamilyDialog';

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  target?: string;
}

interface SubscriptionPlanFamiliesTableProps {
  refreshTrigger?: number;
  bare?: boolean; // when true, render only the table without Card/borders
  data?: SubscriptionPlanFamily[]; // when provided, use this data instead of fetching
  refreshTable?: () => void; // callback to refresh data
}

export default function SubscriptionPlanFamiliesTable({ 
  refreshTrigger = 0, 
  bare = false, 
  data, 
  refreshTable 
}: SubscriptionPlanFamiliesTableProps) {
  const [families, setFamilies] = useState<SubscriptionPlanFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFamily, setEditingFamily] = useState<SubscriptionPlanFamily | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { toast } = useToast();

  const fetchFamilies = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setFamilies(data || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar famílias de planos',
        description: 'Não foi possível carregar as famílias de planos. Por favor, recarregue a página.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (data) {
      setFamilies(data);
      setLoading(false);
    } else {
      fetchFamilies();
    }
  }, [refreshTrigger, data, fetchFamilies]);

  const handleEdit = (family: SubscriptionPlanFamily) => {
    setEditingFamily(family);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('subscription_plan_families')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Família inativada',
        description: 'A família de planos foi inativada com sucesso.',
      });

      if (refreshTable) {
        refreshTable();
      } else {
        setFamilies(families.map(f => 
          f.id === id 
            ? { ...f, is_active: false } 
            : f
        ));
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao inativar família',
        description: 'Não foi possível inativar a família de planos. Por favor, tente novamente.',
      });
    }
  };

  const handleActivate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('subscription_plan_families')
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Família ativada',
        description: 'A família de planos foi ativada com sucesso.',
      });

      if (refreshTable) {
        refreshTable();
      } else {
        setFamilies(families.map(f => 
          f.id === id 
            ? { ...f, is_active: true } 
            : f
        ));
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao ativar família',
        description: 'Não foi possível ativar a família de planos. Por favor, tente novamente.',
      });
    }
  };

  const handleUpdateSuccess = (updatedFamily: SubscriptionPlanFamily) => {
    if (refreshTable) {
      refreshTable();
    } else {
      setFamilies(families.map(f => 
        f.id === updatedFamily.id 
          ? updatedFamily 
          : f
      ));
    }
  };

  const tableMarkup = (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[70%]">Família de Planos</TableHead>
          <TableHead className="w-[12%] text-center">Target</TableHead>
          <TableHead className="w-[10%]">Status</TableHead>
          <TableHead className="w-[8%]">Ações</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {families.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
              Nenhuma família de planos encontrada.
            </TableCell>
          </TableRow>
        ) : (
          families.map((family) => (
            <TableRow key={family.id}>
              <TableCell className="py-2">
                <div className="space-y-0.5">
                  <div className="font-extrabold">{family.name}</div>
                  <div className="text-xs text-muted-foreground">{family.description || '-'}</div>
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap px-2 text-center">
                <div className="text-xs text-muted-foreground">
                  {family.target || '-'}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap px-2">
                <span className={cn(
                  'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-extrabold',
                  family.is_active
                    ? 'bg-green-50 text-green-700'
                    : 'bg-yellow-50 text-yellow-700'
                )}>
                  {family.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </TableCell>
              <TableCell className="px-2">
                <div className="flex items-center justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Ações</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleEdit(family)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      {family.is_active ? (
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => handleDelete(family.id)}
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          Inativar
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem 
                          className="text-green-600"
                          onClick={() => handleActivate(family.id)}
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Ativar
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <>
      {bare ? (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <p className="text-muted-foreground">Carregando famílias de planos...</p>
            </div>
          ) : (
            <div className="rounded-md border">{tableMarkup}</div>
          )}
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Famílias de Planos</CardTitle>
            <CardDescription>
              Gerencie categorias para agrupar planos de assinatura
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <p className="text-muted-foreground">Carregando famílias de planos...</p>
              </div>
            ) : (
              <div className="rounded-md border">
                {tableMarkup}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <EditFamilyDialog
        open={isEditModalOpen}
        onOpenChange={(open) => {
          setIsEditModalOpen(open);
          if (!open) setEditingFamily(null);
        }}
        family={editingFamily}
        onSuccess={handleUpdateSuccess}
      />
    </>
  );
} 